/**
 * professions.ts — the society wave's JOB vocabulary (2026-09-19), pure.
 *
 * `npc_agent.job` is a string column, so the vocabulary grows without a schema
 * change. Three ideas live here:
 *
 *  1. PRODUCER JOBS work NODES. A node is one of the three native kinds a
 *     settlement was seeded with (`food | ore | luxury`, whose raw depends on
 *     the biome) or a SECONDARY DEPOSIT (`dep:<commodity>`, lib/deposits.ts).
 *     `nodeJob` says which trade works a node; `jobWorksNode` is its inverse.
 *     A bard no longer "performs" by harvesting resin: the TAPPER works the
 *     luxury node and the bard performs (a service, lib/service_posts.ts).
 *  2. A job is a PERMISSION, a skill is a PROFICIENCY, and the TITLE a versona
 *     is known by is their highest skill (the Dwarf Fortress rule): a farmer
 *     who holds the baking slot is "a baker". `titleOf` derives it from rows
 *     that already exist (`npc_skill`), so it costs no storage.
 *  3. The job pool FOLLOWS THE PLACE: `supportedJobs` lists the producer trades
 *     a settlement's nodes can employ, and the migration sweep retrains whoever
 *     holds a trade their town cannot use.
 *
 * MIRROR: client/src/lib/professions.ts must stay byte-identical (the Metrics
 * tab, the clip grammar and dialogue read titles from it).
 */

/** Every value `npc_agent.job` may hold. The first seven predate the wave. */
export const JOBS = [
  'farmer', 'miner', 'smith', 'merchant', 'guard', 'bard', 'scout',
  'fisher', 'forester', 'hunter', 'herbalist', 'tapper', 'gas_tender',
] as const;
export type JobV2 = (typeof JOBS)[number];

export const JOB_SET: ReadonlySet<string> = new Set(JOBS);

/** Trades that harvest a node (everyone else keeps a station or a beat). */
export const PRODUCER_JOBS: readonly JobV2[] = ['farmer', 'fisher', 'miner', 'tapper', 'forester', 'hunter', 'herbalist', 'gas_tender'];
const PRODUCER_SET: ReadonlySet<string> = new Set(PRODUCER_JOBS);
export const isProducerJob = (job: string): boolean => PRODUCER_SET.has(job);

/** The native node kinds a settlement is seeded with. */
export type NativeNodeKind = 'food' | 'ore' | 'luxury';
export const DEPOSIT_PREFIX = 'dep:';
export const isDepositGood = (good: string): boolean => good.startsWith(DEPOSIT_PREFIX);
export const depositCommodity = (good: string): string => good.slice(DEPOSIT_PREFIX.length);

/** Which trade harvests each raw commodity. */
const COMMODITY_JOB: Record<string, JobV2> = {
  grain: 'farmer', produce: 'farmer',
  fish: 'fisher',
  ore: 'miner', stone: 'miner', crystal: 'miner', salt: 'miner', noble_ore: 'miner',
  resin: 'tapper', fiber: 'tapper',
  timber: 'forester',
  hide: 'hunter', game: 'hunter',
  herbs: 'herbalist', reagent: 'herbalist', solvent: 'herbalist',
  gas: 'gas_tender',
};

/** The trade that gathers a raw commodity ('' when nothing gathers it). */
export function jobForCommodity(commodity: string): JobV2 | '' {
  return COMMODITY_JOB[commodity] ?? '';
}

/**
 * The trade that works a node. `rawOfNative` is the biome's raw for a native
 * kind (commodities.rawFor) — passed in so this file stays free of the
 * commodity table and the client mirror needs no second import.
 */
export function nodeJob(good: string, rawOfNative: (kind: NativeNodeKind) => string): JobV2 | '' {
  if (isDepositGood(good)) return jobForCommodity(depositCommodity(good));
  if (good === 'food' || good === 'ore' || good === 'luxury') return jobForCommodity(rawOfNative(good));
  return '';
}

/** What a harvest of this node puts in the carry. */
export function nodeCommodity(good: string, rawOfNative: (kind: NativeNodeKind) => string): string {
  if (isDepositGood(good)) return depositCommodity(good);
  if (good === 'food' || good === 'ore' || good === 'luxury') return rawOfNative(good);
  return '';
}

/** A human word for a node, for town news ("the timber stand is picked bare"). */
export function nodeLabel(good: string, rawOfNative: (kind: NativeNodeKind) => string): string {
  const c = nodeCommodity(good, rawOfNative);
  switch (jobForCommodity(c)) {
    case 'farmer': return `${c} grove`;
    case 'fisher': return 'fishing water';
    case 'miner': return `${c.replace(/_/g, ' ')} vein`;
    case 'tapper': return `${c} stand`;
    case 'forester': return 'timber stand';
    case 'hunter': return 'hunting ground';
    case 'herbalist': return `${c} beds`;
    case 'gas_tender': return 'gas vent';
    default: return 'patch';
  }
}

/** The producer trades a settlement's nodes can employ (stable order). */
export function supportedJobs(nodeGoods: readonly string[], rawOfNative: (kind: NativeNodeKind) => string): JobV2[] {
  const out = new Set<JobV2>();
  for (const g of nodeGoods) { const j = nodeJob(g, rawOfNative); if (j) out.add(j); }
  return PRODUCER_JOBS.filter((j) => out.has(j));
}

/** FNV-1a (the sim's one hash — duplicated here so the mirror is standalone). */
function fnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
const h01 = (...parts: Array<string | number | bigint>): number => fnv(parts.join('|')) / 0xffffffff;

/** Share of the entertainer archetype that keeps performing; the rest tap. */
export const BARD_KEEP_SHARE = 0.35;

/**
 * Archetype → job for a NEW versona. Entertainers split between the stage and
 * the luxury stand; civilians spread over the producer trades. The place then
 * corrects the pick (`supportedJobs`) — a forester seeded into a town with no
 * timber retrains on the next sweep.
 */
export function deriveJobV2(archetype: string, versonaId: bigint): JobV2 {
  switch (archetype) {
    case 'vendor': return 'merchant';
    case 'trainer': return 'smith';
    case 'guard': return 'guard';
    case 'entertainer': return h01(versonaId, 'bardsplit') < BARD_KEEP_SHARE ? 'bard' : 'tapper';
    default: {
      const r = h01(versonaId, 'job');
      // The first three bands are the pre-wave split (farmer .45 / miner .30 /
      // scout .15) narrowed, so a re-derived civilian rarely changes class.
      return r < 0.40 ? 'farmer' : r < 0.66 ? 'miner' : r < 0.76 ? 'scout'
        : r < 0.84 ? 'forester' : r < 0.91 ? 'hunter' : r < 0.97 ? 'herbalist' : 'gas_tender';
    }
  }
}

/** Should this sitting bard become a tapper? (the quiet one-time relabel) */
export function bardBecomesTapper(versonaId: bigint): boolean {
  return h01(versonaId, 'bardsplit') >= BARD_KEEP_SHARE;
}

/** BuildingFunction a job works at (the engine's 33-function taxonomy). */
export function jobFunction(job: string): string {
  switch (job) {
    case 'farmer': case 'fisher': case 'forester': case 'tapper': return 'agriculture';
    case 'miner': case 'gas_tender': return 'industry';
    case 'smith': return 'manufacturing';
    case 'merchant': return 'market';
    case 'guard': case 'hunter': return 'defense';
    case 'bard': return 'entertainment';
    case 'scout': case 'herbalist': return 'science';
    default: return 'civic';
  }
}

/** Named town venue a job keeps when it has no node (town_layout slot keys). */
export function jobVenue(job: string): string {
  switch (job) {
    case 'merchant': return 'market';
    case 'smith': return 'forge';
    case 'guard': case 'hunter': return 'guardpost';
    case 'bard': return 'stage';
    case 'farmer': case 'fisher': case 'forester': case 'tapper': return 'granary';
    case 'miner': case 'gas_tender': return 'assay';
    case 'scout': case 'herbalist': return 'archive';
    default: return 'plaza';
  }
}

// ── Titles ───────────────────────────────────────────────────────────────────

/** recipe / post key → the title its practitioner is known by. */
export const TITLE_BY_SKILL: Record<string, string> = {
  // the eleven pre-wave recipes
  mill_flour: 'miller', bake_bread: 'baker', smelt_ingot: 'smelter', forge_tools: 'toolsmith',
  cure_fish: 'fish curer', weave_cloth: 'weaver', sew_garment: 'tailor', cut_crystal: 'lapidary',
  ferment_wine: 'vintner', craft_jewelry: 'jeweller', fest_goods: 'festival maker',
  // consumer staples
  brew_ale: 'brewer', cook_meal: 'cook', mix_medicine: 'apothecary', press_lamp_oil: 'chandler',
  make_paper: 'papermaker', build_cart: 'cartwright',
  // the player components, as versona chains (key = `make_<component>`)
  make_timber_plank: 'sawyer', make_cured_hide: 'tanner', make_stone_block: 'mason', make_ceramic_tile: 'mason',
  make_glass_pane: 'glazier', make_alloy_plate: 'founder', make_reinforced_frame: 'founder',
  make_circuit_lattice: 'lattice-wright', make_insulated_wire: 'lattice-wright', make_power_cell: 'lattice-wright',
  make_blade_billet: 'armourer', make_blaster_barrel: 'gunsmith', make_weapon_grip: 'gunsmith', make_focus_crystal: 'gunsmith',
  make_servo_motor: 'engineer', make_grav_coil: 'engineer', make_thruster_nozzle: 'engineer', make_nav_core: 'engineer',
  make_hull_segment: 'engineer', make_storm_core: 'engineer',
  make_sealant_gel: 'chemist', make_cryo_coolant: 'chemist', make_preserved_rations: 'provisioner',
  // service posts (`svc:<post>`)
  'svc:healer': 'healer', 'svc:teacher': 'teacher', 'svc:publican': 'publican', 'svc:performer': 'performer',
  'svc:acolyte': 'acolyte', 'svc:bath_attendant': 'bath attendant', 'svc:scribe': 'scribe', 'svc:crier': 'town crier',
  'svc:lamplighter': 'lamplighter', 'svc:sweeper': 'street sweeper', 'svc:undertaker': 'undertaker',
  'svc:magistrate': 'magistrate', 'svc:collector': 'collector', 'svc:customs': 'customs officer',
  'svc:warehouse': 'warehouse keeper', 'svc:porter': 'porter', 'svc:builder': 'builder', 'svc:watch': 'watch constable',
  'svc:quartermaster': 'quartermaster', 'svc:surveyor': 'surveyor', 'svc:assayer': 'assayer', 'svc:courier': 'courier',
  'svc:landing_master': 'landing master', 'svc:mechanic': 'mechanic', 'svc:factor': 'long-haul factor', 'svc:ranger': 'ranger',
  'svc:water_carrier': 'water carrier',
};

/** A skill level from which the trade names the person. */
export const TITLE_MIN_SKILL = 1;

/**
 * The title a versona is known by: the practised trade with the highest skill
 * at or above `TITLE_MIN_SKILL`, else the base job. Ties break by key so the
 * answer is stable across clients.
 */
export function titleOf(job: string, skills: ReadonlyArray<{ recipeKey: string; level: number }>): string {
  let best: { key: string; level: number } | null = null;
  for (const s of skills) {
    if (s.level < TITLE_MIN_SKILL || !TITLE_BY_SKILL[s.recipeKey]) continue;
    if (!best || s.level > best.level || (s.level === best.level && s.recipeKey < best.key)) best = { key: s.recipeKey, level: s.level };
  }
  return best ? TITLE_BY_SKILL[best.key] : job.replace(/_/g, ' ');
}

/** Every distinct title the wave can produce (harness coverage + UI legends). */
export const ALL_TITLES: string[] = [...new Set([...JOBS.map((j) => j.replace(/_/g, ' ')), ...Object.values(TITLE_BY_SKILL)])].sort();
